import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ExternalLink } from '@/components/ui/external-link';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { apiClient } from '../services/api';
import { stripeSubscriptionUrl } from '../utils/externalUrls';
import { formatStripeAmount } from '../utils/formatStripe';
import type { DealMapping, LinkCandidatesResponse } from '../types/api';

type LinkTab = 'subscriptions' | 'invoices';

interface LinkExistingDialogProps {
  deal: DealMapping;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The Finrite tenant currently active in the parent iframe view. Included in
  // the tenantSettings query key so switching accounts doesn't serve a stale
  // (previous-tenant) Stripe environment/account for the link candidates.
  activeTenantId?: string;
  // Which pool(s) this deal is still unresolved on. Callers that already know
  // a side is attached (e.g. the iframe deal detail, which tracks isSubAttached
  // / isInvoiceLinked independently) can restrict the dialog to only the
  // unresolved side. Defaults to both, matching prior (dashboard) behavior.
  unresolvedSides?: { subscription: boolean; invoice: boolean };
}

const DEFAULT_UNRESOLVED_SIDES = { subscription: true, invoice: true };

// Claimed candidates are rendered but never selectable, so the default pick
// must skip them — otherwise a pool of only-claimed candidates would preselect
// a radio the user cannot submit.
const firstSelectableSub = (res: LinkCandidatesResponse) =>
  res.candidates.find((c) => c.selectable !== false)?.subscription_id ?? '';

const firstSelectableInvoice = (res: LinkCandidatesResponse) =>
  (res.invoice_candidates ?? []).find((c) => c.selectable !== false)?.invoice_id ?? '';

const LinkExistingDialog: React.FC<LinkExistingDialogProps> = ({
  deal,
  open,
  onOpenChange,
  activeTenantId,
  unresolvedSides = DEFAULT_UNRESOLVED_SIDES,
}) => {
  const queryClient = useQueryClient();
  const dealId = deal.hubspot_deal_id;
  // Already cached by the deals pages — used to build Stripe dashboard links.
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenantSettings', activeTenantId ?? null],
    queryFn: () => apiClient.getTenantSettings(),
    retry: 1,
  });
  const stripeEnv = tenantSettings?.stripe_environment;
  const stripeAcctId = tenantSettings?.stripe_account_id;
  const [data, setData] = useState<LinkCandidatesResponse | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');
  const [manual, setManual] = useState('');
  const [manualInvoice, setManualInvoice] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LinkTab>('subscriptions');

  function extractError(err: unknown, fallback: string): string {
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  }

  useEffect(() => {
    if (!open) {
      // Reset so reopening for a different deal doesn't flash stale candidates.
      setData(null);
      setSelected('');
      setSelectedInvoice('');
      setManual('');
      setManualInvoice('');
      setErrorMsg(null);
      setActiveTab('subscriptions');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        let res = await apiClient.getLinkCandidates(dealId);
        // Never searched yet → run a live check to auto-load ranked candidates.
        if (!res.candidates.length && !res.no_match && !res.searched_at) {
          await apiClient.linkDeal(dealId);
          res = await apiClient.getLinkCandidates(dealId);
        }
        if (cancelled) return;
        setData(res);
        setSelected(res.selected_subscription_id ?? firstSelectableSub(res));
        setSelectedInvoice(
          res.selected_invoice_id ?? firstSelectableInvoice(res),
        );
        // Default to whichever pool actually has candidates when the other
        // doesn't, so the user isn't looking at an empty tab. If the
        // subscription side is already resolved (caller says so), default
        // straight to invoices.
        setActiveTab(
          !unresolvedSides.subscription ||
            (!res.candidates.length && (res.invoice_candidates?.length ?? 0) > 0)
            ? 'invoices'
            : 'subscriptions',
        );
      } catch (err) {
        if (!cancelled) setErrorMsg(extractError(err, 'Failed to load candidates.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // unresolvedSides is read via its primitive fields (below) rather than as
    // a dependency — callers may pass a fresh object literal each render, and
    // this effect should only re-run on open/deal change, not on that reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId, unresolvedSides.subscription]);

  const recheck = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      await apiClient.linkDeal(dealId);
      const res = await apiClient.getLinkCandidates(dealId);
      setData(res);
      setSelected(res.selected_subscription_id ?? firstSelectableSub(res));
      setSelectedInvoice(
        res.selected_invoice_id ?? firstSelectableInvoice(res),
      );
    } catch (err) {
      setErrorMsg(extractError(err, 'Re-check failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    const sub = manual.trim() || selected;
    if (!sub) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await apiClient.selectLinkCandidate(dealId, sub);
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(extractError(err, 'Could not link that subscription. Check the id and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmInvoice = async () => {
    const invoice = manualInvoice.trim() || selectedInvoice;
    if (!invoice) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await apiClient.selectInvoiceLinkCandidate(dealId, invoice);
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(extractError(err, 'Could not link that invoice. Check the id and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const hasSubCandidates = !!data && data.candidates.length > 0;
  const hasInvoiceCandidates = !!data && (data.invoice_candidates?.length ?? 0) > 0;
  const bothSidesUnresolved = unresolvedSides.subscription && unresolvedSides.invoice;
  // Show tabs whenever the deal is unresolved on BOTH sides — regardless of
  // candidate count. A tab with zero candidates still has a manual-entry
  // input, so it's never actually empty/dead.
  const showTabs = bothSidesUnresolved;
  // Single-view cases: only one side is unresolved (caller told us the other
  // is already attached), so there's nothing to tab between.
  const showInvoicesOnly = !showTabs && !unresolvedSides.subscription && unresolvedSides.invoice;

  // Copy reflects what's actually shown: tabs → neutral "either", single-view
  // → name the one thing on offer.
  const dialogTargetLabel = showTabs
    ? 'an existing subscription or invoice'
    : showInvoicesOnly
      ? 'an existing invoice'
      : 'an existing subscription';
  const dialogDescription = showTabs
    ? 'Pick the Stripe subscription or invoice that already exists for this deal, or enter one manually.'
    : showInvoicesOnly
      ? 'Pick the Stripe invoice that already exists for this deal, or enter one manually.'
      : 'Pick the Stripe subscription that already exists for this deal, or enter one manually.';

  const subscriptionList = (
    <>
      {!loading && data && data.candidates.length >= 2 && (
        <Alert>
          <AlertDescription>
            Multiple possible subscriptions matched — review and confirm the correct one.
          </AlertDescription>
        </Alert>
      )}

      {hasSubCandidates && (
        <RadioGroup value={selected} onValueChange={setSelected}>
          {data!.candidates.map((c) => {
            // A subscription links to at most one deal. Claimed ones are shown
            // greyed out with their claiming deal named, rather than hidden —
            // hiding them reads as "no matches" for a subscription the user
            // can see in Stripe.
            const claimed = c.selectable === false;
            return (
              <label
                key={c.subscription_id}
                className={
                  claimed
                    ? 'flex items-center gap-2 py-1 text-sm opacity-60 cursor-not-allowed'
                    : 'flex items-center gap-2 py-1 text-sm cursor-pointer'
                }
              >
                <RadioGroupItem value={c.subscription_id} disabled={claimed} />
                {stripeEnv ? (
                  <ExternalLink
                    href={stripeSubscriptionUrl(stripeEnv, c.subscription_id, stripeAcctId)}
                    className="font-mono"
                  >
                    {c.subscription_id}
                  </ExternalLink>
                ) : (
                  <span className="font-mono">{c.subscription_id}</span>
                )}
                <span className="text-muted-foreground">
                  · {c.status} · matched via {c.matched_via}
                </span>
                {claimed && (
                  <span className="text-muted-foreground">
                    · already linked to deal {c.claimed_by_deal_id}
                  </span>
                )}
              </label>
            );
          })}
        </RadioGroup>
      )}

      {!loading && data && !hasSubCandidates && (
        <p className="text-sm text-muted-foreground py-1">
          No candidates found. Enter a subscription id manually.
        </p>
      )}

      <Input
        placeholder="sub_… (enter a subscription id manually)"
        value={manual}
        onChange={(e) => setManual(e.target.value)}
      />

      {data?.searched_at && (
        <p className="text-xs text-muted-foreground">
          Searched {new Date(data.searched_at).toLocaleString()}. Very recent subscriptions may
          take a moment to appear in Stripe search.
        </p>
      )}
    </>
  );

  const invoiceList = (
    <>
      {!loading && data && (data.invoice_candidates?.length ?? 0) >= 2 && (
        <Alert>
          <AlertDescription>
            Multiple possible invoices matched — review and confirm the correct one.
          </AlertDescription>
        </Alert>
      )}

      {hasInvoiceCandidates && (
        <RadioGroup value={selectedInvoice} onValueChange={setSelectedInvoice}>
          {data!.invoice_candidates!.map((c) => {
            const claimed = c.selectable === false;
            return (
              <label
                key={c.invoice_id}
                className={
                  claimed
                    ? 'flex items-center gap-2 py-1 text-sm opacity-60 cursor-not-allowed'
                    : 'flex items-center gap-2 py-1 text-sm cursor-pointer'
                }
              >
                <RadioGroupItem value={c.invoice_id} disabled={claimed} />
                <span className="font-mono">{c.number ?? c.invoice_id}</span>
                <span className="text-muted-foreground">
                  · {formatStripeAmount(c.amount_due, c.currency)} · {c.status} · matched via{' '}
                  {c.matched_via}
                </span>
                {claimed && (
                  <span className="text-muted-foreground">
                    · already linked to deal {c.claimed_by_deal_id}
                  </span>
                )}
              </label>
            );
          })}
        </RadioGroup>
      )}

      {!loading && data && !hasInvoiceCandidates && (
        <p className="text-sm text-muted-foreground py-1">
          No invoice candidates found. Enter an invoice id manually below.
        </p>
      )}

      <Input
        placeholder="in_… (enter an invoice id manually)"
        value={manualInvoice}
        onChange={(e) => setManualInvoice(e.target.value)}
      />

      {data?.invoice_searched_at && (
        <p className="text-xs text-muted-foreground">
          Searched {new Date(data.invoice_searched_at).toLocaleString()}. Very recent invoices
          may take a moment to appear in Stripe search.
        </p>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link “{deal.dealname ?? dealId}” to {dialogTargetLabel}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching Stripe…
          </div>
        )}

        {errorMsg && (
          <Alert variant="destructive">
            <AlertDescription>{errorMsg}</AlertDescription>
          </Alert>
        )}

        {!loading && showTabs && (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as LinkTab)}>
            <TabsList>
              <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
              <TabsTrigger value="invoices">Invoices</TabsTrigger>
            </TabsList>
            <TabsContent value="subscriptions" className="space-y-2">
              {subscriptionList}
            </TabsContent>
            <TabsContent value="invoices" className="space-y-2">
              {invoiceList}
            </TabsContent>
          </Tabs>
        )}

        {!loading && !showTabs && (showInvoicesOnly ? invoiceList : subscriptionList)}

        <DialogFooter>
          <Button variant="outline" onClick={recheck} disabled={loading || saving}>
            Re-check live
          </Button>
          {(showTabs ? activeTab === 'invoices' : showInvoicesOnly) ? (
            <Button
              onClick={confirmInvoice}
              disabled={loading || saving || (!selectedInvoice && !manualInvoice.trim())}
            >
              Use selected
            </Button>
          ) : (
            <Button onClick={confirm} disabled={loading || saving || (!selected && !manual.trim())}>
              Use selected
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkExistingDialog;
